import { Box, FormControlLabel, Switch } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PreferencesCalendar } from "../../components/PreferencesCalendar/PreferencesCalendar";
import { ShiftsList } from "../../components/ShiftsList/ShiftsList";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import type { DayPreference, NurseProfileToSubmit } from "../../utils/models";
import { useEditNurseProfile } from "../Profile/v2/hooks";
import { getSanitizedNurseData } from "../Profile/v2/util/constants";

export function Preferences() {
	const { trackError } = useAnalytics();
	const { t } = useTranslation();
	const { hospitalData, showErrorSnackbar, chosenDepartment } = useHospitalData();
	const { userInfo } = useAuth();
	const { showSnackbar } = useSnackbar();
	const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.now());
	const [isCalendarOpen, setIsCalendarOpen] = useState(true);
	const [shiftPreferences, setShiftPreferences] = useState<DayPreference[]>([]);
	const [noWeeklyPreferences, setnoWeeklyPreferences] = useState(false);
	const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>(chosenDepartment.department_id);
	// Load initial no_weekly_preferences value
	useEffect(() => {
		if (hospitalData?.nurses && userInfo?.id) {
			const nurse = hospitalData.nurses.find((n: any) => n.userId === userInfo.id);
			if (nurse) {
				setnoWeeklyPreferences(nurse.noWeeklyPreferences || false);
			}
		}
	}, [hospitalData, userInfo?.id]);

	// Update selected department when chosenDepartment changes
	useEffect(() => {
		const newDepartmentId = chosenDepartment.department_id;
		if (newDepartmentId !== selectedDepartmentId) {
			setSelectedDepartmentId(newDepartmentId);

			// Clear current preferences to trigger re-fetch
			setShiftPreferences([]);

			trackEvent("preferences_auto_department_changed", {
				user_id: userInfo?.id,
				previous_department: selectedDepartmentId,
				new_department: newDepartmentId,
			});
		}
	}, [chosenDepartment.department_id, selectedDepartmentId, userInfo?.id]);

	const { editNurseProfileAsync } = useEditNurseProfile();

	const handlenoWeeklyPreferencesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.checked;

		trackEvent("preferences_no_weekly_preferences_toggled", {
			user_id: userInfo?.id,
			new_value: newValue,
		});

		try {
			const nurse = hospitalData?.nurses.find((n) => n.userId === userInfo?.id);
			if (!nurse || !userInfo?.id) {
				showSnackbar(t("unable.find.nurse"), "error");
				trackErrorEvent(
					"preferences_update_failed",
					{
						user_id: userInfo?.id,
						error: "Nurse information not found",
					},
					{ error_type: "data_not_found" },
				);
				return;
			}

			const departmentId = chosenDepartment.department_id;
			if (!departmentId) {
				showSnackbar(t("unable.find.department"), "error");
				trackErrorEvent(
					"preferences_update_failed",
					{
						user_id: userInfo?.id,
						error: "Department information not found",
					},
					{ error_type: "data_not_found" },
				);
				return;
			}

			// Optimistically update the UI
			setnoWeeklyPreferences(newValue);

			// Create a copy of the nurse data with the updated field
			const updatedNurse = {
				...nurse,
				noWeeklyPreferences: newValue,
			};
			const sanitizedNurseData = getSanitizedNurseData(updatedNurse);
			if (!sanitizedNurseData) {
				trackError("sanitize_nurse_error", {
					initialNurseData: JSON.stringify(updatedNurse),
					sanitizedNurseData: sanitizedNurseData,
				});
				return;
			}

			trackEvent("preferences_update_started", {
				user_id: userInfo.id,
				department_id: departmentId,
				update_type: "no_weekly_preferences",
				new_value: newValue,
			});

			await editNurseProfileAsync({
				nurseObjectToEdit: sanitizedNurseData as NurseProfileToSubmit,
			});

			trackEvent("preferences_update_success", {
				user_id: userInfo.id,
				department_id: departmentId,
				update_type: "no_weekly_preferences",
				new_value: newValue,
			});

			showSnackbar(t("preference.updated"), "success");
		} catch (error) {
			showErrorSnackbar(
				t("failed.update.preference"),
				{
					action: "error updating no consistent days preference",
				},
				error,
			);
			trackErrorEvent("preferences_update_failed", error, {
				user_id: userInfo?.id,
				update_type: "no_weekly_preferences",
				attempted_value: newValue,
			});
			// Revert the switch if the API call fails
			setnoWeeklyPreferences(!newValue);
		}
	};

	const handleDateChange = (date: DateTime) => {
		trackEvent("preferences_date_selected", {
			user_id: userInfo?.id,
			previous_date: selectedDate.toFormat("yyyy-MM-dd"),
			new_date: date.toFormat("yyyy-MM-dd"),
		});

		setSelectedDate(date);
	};

	const handleCalendarOpenChange = (isOpen: boolean) => {
		trackEvent("preferences_calendar_toggled", {
			user_id: userInfo?.id,
			is_open: isOpen,
		});

		setIsCalendarOpen(isOpen);
	};

	const handlePreferencesChange = (preferences: DayPreference[]) => {
		trackEvent("preferences_list_updated", {
			user_id: userInfo?.id,
			preference_count: preferences.length,
		});

		setShiftPreferences(preferences);
	};

	return (
		<Box
			sx={{
				height: "calc(100vh - 50px)",
				bgcolor: "#F8F9FC",
				position: "absolute",
				top: "50px",
				left: 0,
				right: 0,
				bottom: 0,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: 0.5,
				overflow: "auto",
				scrollBehavior: "smooth",
				overscrollBehavior: "contain",
				pb: 10,
				maxWidth: "lg",
				mx: "auto",
			}}
		>
			<Box
				sx={{
					position: "sticky",
					top: 0,
					width: "100%",
					zIndex: 3,
					bgcolor: "#F8F9FC",
					pt: 0,
					px: 2,
				}}
			>
				<FormControlLabel
					control={<Switch checked={noWeeklyPreferences} onChange={handlenoWeeklyPreferencesChange} color="primary" />}
					label={t("no.consistent.days.available")}
				/>
			</Box>

			<Box sx={{ position: "relative", width: "100%", flex: 1 }}>
				{noWeeklyPreferences && (
					<Box
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: "rgba(249, 250, 251, 0.65)",
							backdropFilter: "blur(1px)",
							zIndex: 2,
							pointerEvents: "none",
							"&::after": {
								content: '""',
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								background: "rgba(55, 65, 81, 0.05)",
								mixBlendMode: "multiply",
							},
						}}
					/>
				)}
				<Box
					sx={{
						position: "sticky",
						top: 38,
						width: "100%",
						zIndex: 1,
						bgcolor: "#F8F9FC",
						mb: 2,
					}}
				>
					<PreferencesCalendar
						value={selectedDate}
						onChange={handleDateChange}
						isOpen={isCalendarOpen}
						onOpenChange={handleCalendarOpenChange}
						preferences={shiftPreferences}
					/>
				</Box>

				<Box sx={{ position: "relative", zIndex: 0 }}>
					<ShiftsList userId={userInfo?.id || ""} onPreferencesChange={handlePreferencesChange} variant="preferences" />
				</Box>
			</Box>
		</Box>
	);
}
